// Sign-in screen pieces (mockup js/screens/login.js): brand, eye / Google marks, spinner, password rules, card shell,
// and the error readers shared by the sign-in, gate and invite cards.
import { forwardRef, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import { useT } from '../../i18n';
import { cls } from '../../lib/util';
import { Icon } from '../../ui/Icon';

export const MAX_TRIES = 5;
export const LOCK_MIN = 15;

export const detailsOf = (e: ApiError): Record<string, unknown> => (e.details && typeof e.details === 'object' ? (e.details as Record<string, unknown>) : {});
/** When a lock ends: the result's lockedUntil, the error's lockedUntil / retryAfterSec, else 15 minutes from now. */
export function lockedUntilOf(e: ApiError | null, fromResult?: number | null): number {
  if (fromResult && Number.isFinite(fromResult)) return fromResult;
  if (e) {
    const d = detailsOf(e);
    const lu = d.lockedUntil;
    if (typeof lu === 'number' && Number.isFinite(lu)) return lu;
    if (typeof lu === 'string' && !Number.isNaN(Date.parse(lu))) return Date.parse(lu);
    const sec = d.retryAfterSec ?? d.retryAfter;
    if (typeof sec === 'number' && Number.isFinite(sec)) return Date.now() + sec * 1000;
  }
  return Date.now() + LOCK_MIN * 60_000;
}
export const isLockError = (e: ApiError) => e.status === 423 || e.status === 429 || /lock/i.test(e.code);
/** Error codes are compared case-insensitively (older test servers answered INVALID_CODE). */
export const isCode = (e: unknown, code: string): e is ApiError => e instanceof ApiError && e.code.toLowerCase() === code;

export function Brand() {
  const t = useT();
  return (
    <div className="lg-brand">
      <span className="auth-card__mark" aria-hidden="true" />
      <span className="lg-brand__text">
        <b>{t('app.name')}</b>
        <small>{t('app.tagline')}</small>
      </span>
    </div>
  );
}

export function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ic lg-eye__ic" aria-hidden="true" focusable="false">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
      <path className="lg-eye__slash" d="M4 4l16 16" />
    </svg>
  );
}

export function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="ic lg-g" aria-hidden="true" focusable="false">
      <path d="M20.4 12.2c0-.6-.1-1.2-.2-1.7H12v3.3h4.7a4.1 4.1 0 0 1-1.7 2.6" />
      <path d="M15 16.4A6.9 6.9 0 1 1 16.9 7" />
    </svg>
  );
}

export const Spin = () => <i className="lg-spin" aria-hidden="true" />;

/** The centred card (062): shakes once after a wrong password (CSS turns it off with reduced motion). */
export const AuthCard = forwardRef<HTMLElement, { variant?: string; shake?: boolean; onShakeEnd?: () => void; children: ReactNode }>(function AuthCard({ variant, shake, onShakeEnd, children }, ref) {
  return (
    <section
      ref={ref}
      className={cls('auth-card lg-card', variant, shake && 'is-shake')}
      aria-labelledby="lg-title"
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget) onShakeEnd?.();
      }}
    >
      {children}
    </section>
  );
});

/** Password input of the sign-in cards (gate, first admin, invite). */
export function PwField({ id, label, value, onChange, invalid, describedBy, autoComplete = 'new-password' }: { id: string; label: string; value: string; onChange: (v: string) => void; invalid?: boolean; describedBy?: string; autoComplete?: string }) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input className="input lg-input" id={id} type="password" autoComplete={autoComplete} value={value} aria-invalid={invalid || undefined} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/** Text input of the sign-in cards with an optional hint (first admin, invite). */
export function TextField({ id, label, value, onChange, error, hint, ...rest }: { id: string; label: string; value: string; onChange: (v: string) => void; error?: boolean; hint?: ReactNode; type?: string; autoComplete?: string; inputMode?: 'email' | 'text' }) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        className="input lg-input"
        id={id}
        type={rest.type ?? 'text'}
        autoComplete={rest.autoComplete}
        inputMode={rest.inputMode}
        autoCapitalize="none"
        spellCheck={false}
        value={value}
        aria-invalid={error || undefined}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? (
        <p className="field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function CardTitle({ children }: { children: ReactNode }) {
  return (
    <h1 className="auth-card__title" id="lg-title" tabIndex={-1}>
      {children}
    </h1>
  );
}

// ── password rules (plan 11.4.1): 10+ characters · 3 of 4 kinds · no user ID inside · both fields equal ──
const pwClasses = (v: string) => [/[A-Z]/, /[a-z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(v)).length;
function hasId(v: string, loginId: string): boolean {
  const low = String(v || '').toLowerCase();
  const local = String(loginId || '').split('@')[0]!.toLowerCase();
  if (!low || !local) return false;
  return low.includes(local) || local.split(/[._-]/).some((p) => p.length >= 4 && low.includes(p));
}
export type PwRule = 'len' | 'mix' | 'id' | 'match';
export function pwRules(p1: string, p2: string, loginId: string): Array<[PwRule, boolean]> {
  return [
    ['len', (p1 || '').length >= 10],
    ['mix', pwClasses(p1 || '') >= 3],
    ['id', !!p1 && !hasId(p1, loginId)],
    ['match', !!p1 && p1 === p2],
  ];
}
export const pwRulesOk = (p1: string, p2: string, loginId: string): boolean => pwRules(p1, p2, loginId).every((r) => r[1]);

export function PasswordRules({ id, p1, p2, loginId }: { id: string; p1: string; p2: string; loginId: string }) {
  const t = useT();
  return (
    <ul className="lg-rules" id={id} aria-label={t('login.gate.pw.rules')}>
      {pwRules(p1, p2, loginId).map(([k, ok]) => (
        <li key={k} className={ok ? 'is-ok' : undefined}>
          <Icon name={ok ? 'check' : 'dot'} />
          <span>{t('login.gate.pw.rule.' + k)}</span>
          <span className="visually-hidden">{' · ' + t(ok ? 'login.gate.pw.ruleOk' : 'login.gate.pw.ruleNo')}</span>
        </li>
      ))}
    </ul>
  );
}

/** m:ss of a remaining time. */
export function mmss(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? '0' : ''}${r}`;
}

/** Only same-site relative paths may be a "next" address (never another site, never back to /login). */
export function safeNext(next: string | null | undefined): string | null {
  if (!next || !/^\/(?!\/)/.test(next) || /^\/login(?:[/?#]|$)/.test(next) || /[\s\\]/.test(next)) return null;
  return next;
}
